import {
  CONTAINER_LOBS,
  workTypesFor,
  type ContainerAggregateRow,
  type ContainerConfig,
  type ContainerDetailRow,
  type ContainerLob,
} from "./containerInventory.mjs";
import { fetchAllPages, isTableMissingMessage } from "./execKpiFetch.mjs";
import { isColumnMissingError } from "./sanityGate";
import { LiveCache, refreshTogether, useLiveCacheVersion } from "./liveCacheHook";
import { getSupabase } from "./supabase";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * Containers tab: data access. Thin on purpose; every decision about what a
 * row means is in containerInventory.mjs.
 *
 * ONE READ PER LINE OF BUSINESS PER PAGE LOAD. The tab's widgets all read the
 * same counts, so the aggregate is fetched once per (tenant, config) and
 * shared through a module-level promise. That also makes the widgets agree by
 * construction: the snapshot, the summary and the tiles are cut from one set
 * of rows.
 *
 * PostgREST groups by every plain column in the select and counts event_id per
 * group, so a page is at most one row per (scheduled date, division, work
 * type, container size), not one per work order. route_id joins the grouping
 * only on a line whose customer excludes a route, so the exclusion is applied
 * to the route name itself rather than approximated with a LIKE pattern.
 */

const TABLE = "service_event";
const PAGE_SIZE = 1000;
const MAX_PAGES = 200;
const DETAIL_MAX_PAGES = 50;

function isMissing(message: string): boolean {
  return isTableMissingMessage(message) || isColumnMissingError(message);
}

async function fetchLine(
  tenantId: string,
  lob: ContainerLob,
  workTypes: string[],
  withRoute: boolean,
): Promise<ContainerAggregateRow[]> {
  if (workTypes.length === 0) return [];
  const supabase = getSupabase();
  const groupCols = ["event_date", "division", "work_type", "volume_collected_cy"];
  if (withRoute) groupCols.push("route_id");
  const select = `${groupCols.join(",")},event_id.count()`;
  const result = await fetchAllPages<Record<string, unknown>>(
    (from: number, to: number) => {
      let q: any = scopeTenant((supabase.from(TABLE) as any).select(select), tenantId);
      q = q.eq("equipment_type", lob).eq("is_completed", 1).in("work_type", workTypes);
      for (const col of groupCols) q = q.order(col, { ascending: true, nullsFirst: true });
      return q.range(from, to);
    },
    { pageSize: PAGE_SIZE, maxPages: MAX_PAGES, isMissing },
  );
  if (result.truncated) {
    throw new Error(
      `Container counts for ${lob} exceeded ${PAGE_SIZE * MAX_PAGES} grouped rows; not shown ` +
        "rather than shown partially.",
    );
  }
  return result.rows.map((row) => ({
    lob,
    event_date: String(row.event_date ?? ""),
    division: (row.division as string | null) ?? null,
    work_type: String(row.work_type ?? ""),
    volume_collected_cy: (row.volume_collected_cy as number | string | null) ?? null,
    route_id: withRoute ? ((row.route_id as string | null) ?? null) : undefined,
    count: (row.count as number | string) ?? 0,
  }));
}

const aggregateCache = new LiveCache<ContainerAggregateRow[]>();

/** Every grouped count the tab reads, for the lines the customer configured. */
export function fetchContainerAggregates(
  tenantId: string,
  cfg: ContainerConfig,
): Promise<ContainerAggregateRow[]> {
  const key = `${tenantId}|${JSON.stringify(cfg)}`;
  // A failed read must not stick: the next mount retries.
  return aggregateCache.get(key, () => readAggregates(tenantId, cfg));
}

function readAggregates(tenantId: string, cfg: ContainerConfig): Promise<ContainerAggregateRow[]> {
  return Promise.all(
    CONTAINER_LOBS.filter((lob) => cfg[lob]).map((lob) => {
      const lobCfg = cfg[lob];
      return fetchLine(
        tenantId,
        lob,
        workTypesFor(lobCfg),
        (lobCfg?.exclude_route_prefixes.length ?? 0) > 0,
      );
    }),
  ).then((parts) => parts.flat());
}

/** The two work-order columns MER-855 adds (PR #676, migration 0083). */
export const SITE_COLUMNS = ["site_id", "site_name"] as const;

export interface ContainerDetailResult {
  /** False until the site columns exist on service_event. */
  columnsPresent: boolean;
  rows: ContainerDetailRow[];
}

const detailCache = new LiveCache<ContainerDetailResult>();

/**
 * Row-level work orders for one month (the detail tables and the daily site
 * list). Asks for the site columns; a missing column (42703 before the MER-855
 * migration is applied) is the "awaiting source data" state, not an error.
 */
export function fetchContainerDetail(
  tenantId: string,
  cfg: ContainerConfig,
  month: string,
): Promise<ContainerDetailResult> {
  const key = `${tenantId}|${month}|${JSON.stringify(cfg)}`;
  return detailCache.get(key, () => readDetail(tenantId, cfg, month));
}

function readDetail(
  tenantId: string,
  cfg: ContainerConfig,
  month: string,
): Promise<ContainerDetailResult> {
  const [y, m] = month.split("-").map(Number);
  const start = `${month}-01`;
  const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
  const supabase = getSupabase();
  const cols = [
    "event_id",
    "event_date",
    "division",
    "work_type",
    "volume_collected_cy",
    "route_id",
    ...SITE_COLUMNS,
  ];
  return (async (): Promise<ContainerDetailResult> => {
    const rows: ContainerDetailRow[] = [];
    for (const lob of CONTAINER_LOBS) {
      const lobCfg = cfg[lob];
      if (!lobCfg) continue;
      const workTypes = workTypesFor(lobCfg);
      if (workTypes.length === 0) continue;
      try {
        const result = await fetchAllPages<Record<string, unknown>>(
          (from: number, to: number) => {
            let q: any = scopeTenant(
              (supabase.from(TABLE) as any).select(cols.join(",")),
              tenantId,
            );
            q = q
              .eq("equipment_type", lob)
              .eq("is_completed", 1)
              .in("work_type", workTypes)
              .gte("event_date", start)
              .lt("event_date", next)
              .order("event_date", { ascending: true })
              .order("event_id", { ascending: true });
            return q.range(from, to);
          },
          { pageSize: PAGE_SIZE, maxPages: DETAIL_MAX_PAGES, isMissing },
        );
        if (result.truncated) {
          // Withheld, never shown partially: a cut-off list reads as complete.
          throw new Error(
            `Container work orders for ${lob} in ${month} exceeded ` +
              `${PAGE_SIZE * DETAIL_MAX_PAGES} rows; not shown rather than shown partially.`,
          );
        }
        for (const row of result.rows) {
          rows.push({
            lob,
            event_id: (row.event_id as string | null) ?? null,
            event_date: String(row.event_date ?? ""),
            division: (row.division as string | null) ?? null,
            work_type: String(row.work_type ?? ""),
            volume_collected_cy: (row.volume_collected_cy as number | string | null) ?? null,
            route_id: (row.route_id as string | null) ?? null,
            site_id: (row.site_id as string | null) ?? null,
            site_name: (row.site_name as string | null) ?? null,
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        const tableMissing = (err as { tableMissing?: boolean } | null)?.tableMissing === true;
        if (tableMissing || isColumnMissingError(message)) {
          return { columnsPresent: false, rows: [] };
        }
        throw err;
      }
    }
    return { columnsPresent: true, rows };
  })();
}

/**
 * AJ 2026-09-30 live refresh: re-read the tenant's grouped counts and every
 * month of detail already read, and swap them in together (useLiveReport).
 * Rejects, keeping the old answers, when any read fails.
 */
export function refreshContainers(tenantId: string): Promise<void> {
  return refreshTogether([aggregateCache, detailCache], tenantId);
}

/** Bumps when refreshContainers swaps new answers in: an effect dependency. */
export function useContainerDataVersion(): number {
  return useLiveCacheVersion(aggregateCache) + useLiveCacheVersion(detailCache);
}
